import { Play, MapPin, Edit2 } from "lucide-react";
import { OnboardingPhoto, OnboardingVideo, OnboardingData } from "@/hooks/useOnboarding";

interface OnboardingPreviewProps {
  photos: OnboardingPhoto[];
  video: OnboardingVideo | null;
  data: OnboardingData;
  onEditPhotos: () => void;
  onEditVideo: () => void;
  onEditInfo: () => void;
}

const OnboardingPreview = ({
  photos,
  video,
  data,
  onEditPhotos,
  onEditVideo,
  onEditInfo,
}: OnboardingPreviewProps) => {
  const mainPhoto = photos[0];

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-bold">Looking Good! 🎉</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Preview your profile before going live
        </p>
      </div>

      {/* Profile Preview Card */}
      <div className="relative aspect-[3/4] max-w-[280px] mx-auto rounded-3xl overflow-hidden bg-card shadow-balloon">
        {/* Main photo */}
        {mainPhoto && (
          <img
            src={mainPhoto.preview}
            alt="Profile"
            className="w-full h-full object-cover"
          />
        )}

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

        {/* Video thumbnail indicator */}
        {video && (
          <div className="absolute top-4 right-4 w-14 h-20 rounded-xl overflow-hidden bg-black/50 backdrop-blur-sm border-2 border-white/30">
            <video
              src={video.preview}
              className="w-full h-full object-cover"
              muted
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <Play className="w-5 h-5 text-white fill-white" />
            </div>
          </div>
        )}

        {/* Profile info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
          <h4 className="text-2xl font-bold">{data.displayName || "Your Name"}</h4>
          {data.cityName && data.countryName && (
            <div className="flex items-center gap-1 mt-1 text-white/80">
              <MapPin className="w-4 h-4" />
              <span className="text-sm">
                {data.cityName}, {data.countryName}
              </span>
            </div>
          )}
        </div>

        {/* Photo count indicator */}
        {photos.length > 1 && (
          <div className="absolute top-4 left-4 flex gap-1">
            {photos.map((_, i) => (
              <div
                key={i}
                className={`w-6 h-1 rounded-full ${
                  i === 0 ? "bg-white" : "bg-white/40"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Edit buttons */}
      <div className="flex flex-col gap-2 max-w-[280px] mx-auto">
        <button
          onClick={onEditPhotos}
          className="flex items-center justify-between w-full py-3 px-4 rounded-xl bg-muted hover:bg-muted/80 transition-colors"
        >
          <span className="font-medium">Edit Photos</span>
          <div className="flex items-center gap-2 text-muted-foreground">
            <span className="text-sm">{photos.length} photo{photos.length !== 1 ? "s" : ""}</span>
            <Edit2 className="w-4 h-4" />
          </div>
        </button>

        <button
          onClick={onEditVideo}
          className="flex items-center justify-between w-full py-3 px-4 rounded-xl bg-muted hover:bg-muted/80 transition-colors"
        >
          <span className="font-medium">Edit Video</span>
          <div className="flex items-center gap-2 text-muted-foreground">
            <span className="text-sm">{video ? "Added" : "None"}</span>
            <Edit2 className="w-4 h-4" />
          </div>
        </button>

        <button
          onClick={onEditInfo}
          className="flex items-center justify-between w-full py-3 px-4 rounded-xl bg-muted hover:bg-muted/80 transition-colors"
        >
          <span className="font-medium">Change Info</span>
          <Edit2 className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>
    </div>
  );
};

export default OnboardingPreview;
